import { useEffect, useMemo, useState } from "react";
import { http } from "../../lib/api";
import { fValor } from "../../lib/trading";
import { TC, TvChart } from "./TvChart";
import { TradingPanel, agregar, cambioRango, recortar, tonoDe } from "./Panel";
import { VistaSelector } from "./VistaSelector";

const M = (v) => fValor(v, "money");
const P2 = (v) => `${(v * 100).toFixed(2)}%`;
const dPct = (d, a) => (a ? `${d > 0 ? "+" : ""}${((d / Math.abs(a)) * 100).toFixed(2)}%` : "");
const dPp = (d) => `${d > 0 ? "+" : ""}${(d * 100).toFixed(2)} pp`;
const pts = (s, iv, modo) => agregar(s || [], iv, modo);

const useSeries = (keys, dias = 1000) => {
  const [d, setD] = useState({});
  useEffect(() => {
    keys.forEach((k) => http.get(`/ticker/serie/${encodeURIComponent(k)}`, { params: { dias } }).then((r) => setD((x) => ({ ...x, [k]: r.data }))).catch(() => setD((x) => ({ ...x, [k]: null }))));
  }, [keys.join(","), dias]); // eslint-disable-line react-hooks/exhaustive-deps
  return d;
};

export const SaldoPanel = ({ corte }) => {
  const s = useSeries(["saldo", "orig_dia", "cob_dia"]);
  const [r, setR] = useState(0);
  const [iv, setIv] = useState("S");
  const [vela, setVela] = useState(false);
  const base = recortar(s.saldo || [], r);
  const serie = useMemo(() => {
    if (!s.saldo) return null;
    const lim = base[0]?.fecha || "";
    const flujo = (k) => pts((s[k] || []).filter((p) => p.fecha >= lim), iv, "sum");
    return [{ id: "saldo", name: "Saldo", type: vela ? "candle" : "area", color: TC.up, data: pts(base, iv, vela ? "ohlc" : "last"), fmt: M },
      { id: "orig", name: "Originación", type: "histogram", color: TC.blue, data: flujo("orig_dia"), pane: 1, fmt: M },
      { id: "cob", name: "Cobranza", type: "histogram", color: TC.accent, data: flujo("cob_dia"), pane: 1, fmt: M }];
  }, [s, r, iv, vela]); // eslint-disable-line react-hooks/exhaustive-deps
  const mk = useMemo(() => (corte && serie ? (() => { const t = serie[0].data.find((p) => p.time >= corte.slice(0, 10)) || serie[0].data[serie[0].data.length - 1]; return t ? [{ id: "saldo", time: t.time, text: "Corte" }] : []; })() : []), [serie, corte]);
  if (s.saldo === null) return null;
  const c = cambioRango(base, dPct);
  return (
    <TradingPanel title="Saldo de cartera" ultimo={base.length ? fValor(base[base.length - 1].valor, "money", true) : "—"} cambio={c.txt} tono={tonoDe(c.dir, "up_good")} rango={r} setRango={setR} iv={iv} setIv={setIv} corte={corte}
      extra={<button type="button" onClick={() => setVela(!vela)} className="trd-mono rounded-md border border-[var(--trd-border)] px-2 py-0.5 text-[11px] text-[var(--trd-muted)] hover:text-white" data-testid="tp-saldo-vela">{vela ? "Área" : "Velas"}</button>}
      como="Saldo diario reconstruido con el mismo cálculo de Loan Management (monto dispuesto − capital pagado al día). Velas: apertura = cierre del periodo anterior. Panel inferior: originación (fecha de disposición) y cobranza real (capital + interés) sumadas por periodo."
      fuente="Loan Management · /api/ticker/serie" testid="tp-saldo">
      {serie ? <TvChart series={serie} markers={mk} height={300} fmt={M} testid="tp-saldo-chart" /> : <div className="h-[300px] animate-pulse rounded-lg bg-[var(--trd-surface)]" />}
    </TradingPanel>
  );
};

export const TasaTiiePanel = () => {
  const s = useSeries(["tasa", "tiie"], 1000);
  const [r, setR] = useState(182);
  if (!s.tasa || !s.tiie) return null;
  const t = recortar(s.tasa, r), lim = t[0]?.fecha || "", ti = s.tiie.filter((p) => p.fecha >= lim);
  const sp = t.length && ti.length ? t[t.length - 1].valor - ti[ti.length - 1].valor : null;
  const serie = [{ id: "tasa", name: "Tasa pond.", type: "area", color: TC.up, data: pts(t, "D"), fmt: P2 },
    { id: "tiie", name: "TIIE 28", type: "area", color: TC.accent, fill: TC.bg, data: pts(ti, "D"), fmt: P2 }];
  return (
    <TradingPanel title="Tasa promedio ponderada vs TIIE 28" ultimo={sp == null ? "—" : `Spread ${dPp(sp)}`} rango={r} setRango={setR} testid="tp-spread"
      como="Tasa = Σ(tasa efectiva anual × saldo) ÷ Σ saldo. TIIE 28 del DOF. El área sombreada entre ambas líneas es el spread (tasa − TIIE)." fuente="Loan Management + DOF SIDOF 165">
      <TvChart series={serie} height={240} fmt={P2} testid="tp-spread-chart" />
    </TradingPanel>
  );
};

export const ImorPanel = ({ corte }) => {
  const s = useSeries(["vencida"]);
  const [r, setR] = useState(0);
  if (!s.vencida) return null;
  const b = recortar(s.vencida, r), c = cambioRango(b, dPp);
  return (
    <TradingPanel title="Cartera vencida (IMOR)" ultimo={b.length ? P2(b[b.length - 1].valor) : "—"} cambio={c.txt} tono={tonoDe(c.dir, "up_bad")} rango={r} setRango={setR} corte={corte} testid="tp-imor"
      como="Saldo de créditos con 90 o más días de atraso ÷ saldo total, por día (misma definición que Inicio/Loan Management)." fuente="Loan Management · DPD por calendario">
      <TvChart series={[{ id: "imor", name: "IMOR", type: "line", color: TC.down, data: pts(b, "D"), fmt: P2 }]} height={200} fmt={P2} testid="tp-imor-chart" />
    </TradingPanel>
  );
};

const Mini = ({ k, title, f, como }) => {
  const s = useSeries([k], 730);
  const [r, setR] = useState(91);
  if (!s[k]) return null;
  const b = recortar(s[k], r), c = cambioRango(b, dPct);
  return (
    <TradingPanel title={title} ultimo={b.length ? f(b[b.length - 1].valor) : "—"} cambio={c.txt} tono="flat" rango={r} setRango={setR} como={como} fuente="DOF · SIDOF" testid={`tp-${k}`}>
      <TvChart series={[{ id: k, name: title, type: "area", color: TC.cyan, data: pts(b, "D"), fmt: f }]} height={150} fmt={f} testid={`tp-${k}-chart`} />
    </TradingPanel>
  );
};

export const MercadoMini = () => (
  <div className="grid gap-3 md:grid-cols-2">
    <Mini k="fix" title="USD/MXN FIX" f={(v) => v.toFixed(4)} como="Tipo de cambio FIX publicado en el DOF (respaldo: FIX Banxico de Loan Management)." />
    <Mini k="udi" title="UDI" f={(v) => v.toFixed(6)} como="Valor diario de la UDI publicado en el DOF." />
  </div>
);

// Inicio: gráficas trading según el alcance del usuario (el servidor decide qué series puede ver).
export const GraficasCartera = () => {
  const [t, setT] = useState(null);
  useEffect(() => { http.get("/ticker").then((r) => setT(r.data)).catch(() => setT({ items: [] })); }, []);
  if (!t) return null;
  const k = new Set(t.items.map((i) => i.key));
  return (
    <VistaSelector testid="inicio-vista">
      <div className="space-y-3" data-testid="graficas-trading">
        {k.has("saldo") && <SaldoPanel corte={t.corte} />}
        {(k.has("tasa") || k.has("vencida")) && <div className="grid gap-3 lg:grid-cols-2">{k.has("tasa") && <TasaTiiePanel />}{k.has("vencida") && <ImorPanel corte={t.corte} />}</div>}
        <MercadoMini />
      </div>
    </VistaSelector>
  );
};
